import { useEffect, useState, useRef, useCallback } from "react";
import { RkflPlugin } from '@rkfl/transact-client';
import { environment as envConfig } from "../../utils/constants";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { showError, showSuccess, showErrorWithStatus } from "../../utils/alert";
import { faCartShopping, faLock, faChevronLeft } from "@fortawesome/free-solid-svg-icons";
import { faGear } from "@fortawesome/free-solid-svg-icons/faGear";
import { useNavigate } from "react-router-dom";
import Loading from "../../components/Loader";
import useClearCaches from "../../utils/caches";

// SDK instances will be created inside the component to react to localStorage changes

const videoCatalog = {
  vid1: {
    id: "vid1",
    name: "Royal Drama: Season Finale",
    price: 2.00,
    duration: "1:15:30",
    restricted: true,
    thumb: "https://cdn.pixabay.com/photo/2016/11/29/09/16/architecture-1868667_640.jpg",
  },
  vid2: {
    id: "vid2",
    name: "Sci-Fi Mystery: Behind the Scenes",
    price: 1.50,
    duration: "25:45",
    restricted: true,
    thumb: "https://cdn.pixabay.com/photo/2017/08/30/01/05/milky-way-2695569_640.jpg",
  },
  vid3: {
    id: "vid3",
    name: "Romance: Special Episode",
    price: 1.75,
    duration: "42:15",
    restricted: true,
    thumb: "https://cdn.pixabay.com/photo/2016/11/29/09/16/architecture-1868667_640.jpg",
  },
  vid4: {
    id: "vid4",
    name: "Crime Thriller: Director's Cut",
    price: 2.00,
    duration: "1:28:20",
    restricted: true,
    thumb: "https://cdn.pixabay.com/photo/2017/08/30/01/05/milky-way-2695569_640.jpg",
  },
  vid5: {
    id: "vid5",
    name: "Fantasy Adventure: Making Of",
    price: 1.85,
    duration: "58:45",
    restricted: true,
    thumb: "https://cdn.pixabay.com/photo/2016/11/29/09/16/architecture-1868667_640.jpg",
  },
  vid6: {
    id: "vid6",
    name: "Heist Series: Character Backstory",
    price: 1.95,
    duration: "38:18",
    restricted: true,
    thumb: "https://cdn.pixabay.com/photo/2017/08/30/01/05/milky-way-2695569_640.jpg",
  },
};

export default function Home() {
  useClearCaches();
  const [cartCount, setCartCount] = useState(0);
  const navigate = useNavigate();
  const [verified, setVerified] = useState(
    typeof window !== "undefined" ? localStorage.getItem("verified") === "1" : false
  );
  const [sdk, setSdk] = useState(null);
  const [cart, setCart] = useState([]);
  const [show, setShow] = useState(false);
  const hasAutoLaunched = useRef(false);
  const [ageVerificationEnabled, setAgeVerificationEnabled] = useState(
    typeof window !== "undefined" ? localStorage.getItem("ageVerificationEnabled") !== "false" : true
  );
  const [toggleExpanded, setToggleExpanded] = useState(false);
  const [currentEnvironment, setCurrentEnvironment] = useState(
    typeof window !== "undefined" ? localStorage.getItem("rkfl_environment") || "production" : "production"
  );

  const initializeSDK = useCallback(() => {
    const nationality = JSON.parse(localStorage.getItem("rkfl_nationality") || "[]");
    const env = localStorage.getItem("rkfl_environment") || "production";

    // Update current environment state only if it changed
    setCurrentEnvironment(prevEnv => {
      if (prevEnv !== env) {
        hasAutoLaunched.current = false; // Reset auto-launch flag when environment changes
        // Remove access when environment changes
        if (typeof window !== "undefined") {
          localStorage.removeItem('access');
        }
        return env;
      }
      return prevEnv;
    });

    const ageplugin = {
      feature: "AGE_VERIFICATION",
      containerId: "verification-container",
      inject: false,
      countries: nationality,
    };

    let newSdk;
    if (env === 'production') {
      newSdk = new RkflPlugin({
        plugins: [ageplugin],
        clientId: envConfig.production.clientId,
      });
    } else {
      newSdk = new RkflPlugin({
        plugins: [ageplugin],
        environment: 'sandbox',
        clientId: envConfig.sandbox.clientId,
      });
    }

    // Set SDK in a single update
    setSdk(newSdk);
  }, []);

  const renderCheckout = useCallback(() => {
    const raw = localStorage.getItem("cart");
    if (!raw) return;
    try {
      setCart(JSON.parse(raw).cart || []);
    } catch { }
  }, []);

  // Initialize SDK on mount and when environment/nationality changes via events
  useEffect(() => {
    initializeSDK();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount, re-initialization happens via event listeners

  // Handle localStorage changes and message events
  useEffect(() => {
    // Listen for localStorage changes
    const handleStorageChange = (e) => {
      if (e.key === "rkfl_environment") {
        // Remove access when environment changes
        if (typeof window !== "undefined") {
          localStorage.removeItem('access');
        }
        initializeSDK();
      } else if (e.key === "rkfl_nationality") {
        initializeSDK();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    // Also listen for changes within the same tab
    const handleCustomStorageChange = () => {
      // Remove access when environment changes (detected via custom event)
      if (typeof window !== "undefined") {
        localStorage.removeItem('access');
      }
      initializeSDK();
    };

    window.addEventListener('localStorageChange', handleCustomStorageChange);

    const handleMessage = (event) => {
      if (event.data.type === "AGE_VERIFICATION" && ageVerificationEnabled) {
        let client = sdk;
        if (!client) return; // Don't process if SDK not ready
        setTimeout(() => {
          client.verifyAgeVerification(event.data.auditId).then(res => {
            const data = res.data.result;
            if (data.status === 'status_verified') {
              localStorage.setItem("verified", event.data.verified ? "1" : "0");
              setVerified(event.data.verified);
            } else {
              // show a centralized popup with error and current status
              showErrorWithStatus(
                "Age verification could not be completed successfully.",
                data.status
              );
            }
          }).catch(err => { console.log(err) })
        }, 3000);
      }
      if (event.data.type === 'AUDIT_ID_GENERATED') {
        console.log('Audit Id generated ', event.data)
      }
    };
    window.addEventListener('message', handleMessage)

    return () => {
      window.removeEventListener("message", handleMessage);
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('localStorageChange', handleCustomStorageChange);
    };
  }, [sdk, ageVerificationEnabled, initializeSDK]);

  // Initial setup - only run once
  useEffect(() => {
    updateCartCount();
    renderCheckout();
  }, [renderCheckout]);

  // Auto-launch age verification widget on page load if verification is not completed
  useEffect(() => {
    const launchWidgetIfNotVerified = async () => {
      if (sdk && !verified && !hasAutoLaunched.current && ageVerificationEnabled) {
        try {
          hasAutoLaunched.current = true;
          setShow(true);
          await sdk.init();
          sdk.launchAgeVerificationWidget();
          setShow(false);
        } catch (err) {
          console.error("Error auto-launching age verification widget:", err);
          setShow(false);
          hasAutoLaunched.current = false; // Reset on error so user can retry
        }
      }
    };

    launchWidgetIfNotVerified();
  }, [sdk, verified, ageVerificationEnabled]);

  const toggleAgeVerification = () => {
    if (verified) return; // Disable if already verified
    const newValue = !ageVerificationEnabled;
    setAgeVerificationEnabled(newValue);
    if (typeof window !== "undefined") {
      localStorage.setItem("ageVerificationEnabled", newValue ? "true" : "false");
    }
  };

  function updateCartCount() {
    const raw = localStorage.getItem("cart");
    if (!raw) return;
    try {
      const data = JSON.parse(raw);
      const totalQty = Array.isArray(data.cart)
        ? data.cart.reduce((sum, item) => sum + (item.quantity || 0), 0)
        : 0;
      setCartCount(totalQty);
    } catch { }
  }

  function addToCart(vid) {
    localStorage.removeItem("uuid");
    const raw = localStorage.getItem("cart");
    const cartObj = raw
      ? JSON.parse(raw)
      : {
        amount: "0",
        currency: "USD",
        cart: [],
        customerInfo: { name: "John Doe", email: "john.doe@example.com" },
        customParameter: { returnMethod: "GET", params: [{ name: "submerchant", value: "streamflix" }] },
        merchant_id: localStorage.getItem("rkfl_merchantId"),
      };

    const idx = cartObj.cart.findIndex((item) => item.id === vid);
    if (idx >= 0) {
      if (cartObj.cart[idx].quantity < 2) {
        cartObj.cart[idx].quantity++;
      } else {
        showError("Maximum quantity for this item is 2.");
        return;
      }
    } else {
      const v = { ...videoCatalog[vid], price: videoCatalog[vid].price?.toString(), quantity: 1 };
      delete (v).restricted;
      delete (v).duration;
      delete (v).thumb;

      cartObj.cart.push(v);
    }
    cartObj.amount = String(
      cartObj.cart.reduce((sum, v) => sum + Number(v.price) * v.quantity, 0)
    );
    localStorage.setItem("cart", JSON.stringify(cartObj));
    updateCartCount();
    renderCheckout();
    showSuccess(videoCatalog[vid].name + " added to cart.");
  }

  const handleAge = async () => {
    if (!ageVerificationEnabled) return;
    try {
      setShow(true);
      if (sdk) {
        await sdk.init();
        sdk.launchAgeVerificationWidget();
      } else {
        console.error("SDK not initialized");
        showError("SDK not initialized. Please refresh the page.");
      }
      setShow(false);
    } catch (err) {
      console.error("Error initializing age verification plugin:", err);
      setShow(false);
      return;
    }
  }
  return (
    <div>
      {/* Navbar */}
      <Loading show={show} message="Initializing Widget... " />
      <nav className="navbar navbar-light bg-white shadow-sm mb-4 justify-between px-3 flex">
        <div className="brand-title">🎬 StreamFlix</div>
        <div className="d-flex align-items-center gap-3">
          <div className="rounded-pill bg-dark text-light px-4 py-1 text-capitalize">
            {currentEnvironment}
          </div>
          <button onClick={() => navigate("/checkout")} className="cart-wrapper btn btn-text">
            <FontAwesomeIcon icon={faCartShopping} />
            {cartCount > 0 && <span className="cart-badge">{cartCount}</span>}
          </button>
          <button onClick={() => navigate("/settings")} className="btn btn-outline-primary btn-sm d-flex align-items-center">
            <FontAwesomeIcon icon={faGear} />Settings
          </button>
          <div id="verification-container"></div>
        </div>
      </nav>

      {/* Age Verification Toggle - Shows glimpse by default, expands on click */}
      <div
        style={{
          position: 'fixed',
          right: '0px',
          top: '50%',
          transform: 'translateY(-50%)',
          zIndex: 1000,
        }}
        className="age-verification-toggle"
      >
        <div
          className="toggle-container"
          onClick={() => !toggleExpanded && setToggleExpanded(true)}
          style={{
            background: '#fff',
            borderRadius: '12px 0 0 12px',
            padding: '16px 5px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            border: '1px solid #e0e0e0',
            borderRight: 'none',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: '12px',
            width: toggleExpanded ? '280px' : '60px',
            right: toggleExpanded ? '0px' : '-30px',
            opacity: toggleExpanded ? '1' : '0.8',
            transition: 'all 0.3s ease',
            position: 'relative',
            overflow: 'hidden',
            cursor: !toggleExpanded ? 'pointer' : 'default',
          }}
        >
          {/* Arrow button to expand/collapse */}
          <div
            onClick={(e) => {
              e.stopPropagation();
              setToggleExpanded(!toggleExpanded);
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              padding: '4px',
              flexShrink: 0,
            }}
          >
            <FontAwesomeIcon
              icon={faChevronLeft}
              style={{
                fontSize: '14px',
                color: '#666',
                transform: toggleExpanded ? 'rotate(0deg)' : 'rotate(180deg)',
                transition: 'transform 0.3s ease',
              }}
            />
          </div>

          {/* Toggle switch */}
          <div
            onClick={(e) => {
              e.stopPropagation();
              if (!verified) toggleAgeVerification();
            }}
            style={{
              width: '50px',
              height: '26px',
              background: verified ? '#6c757d' : (ageVerificationEnabled ? '#28a745' : '#ccc'),
              borderRadius: '13px',
              position: 'relative',
              cursor: verified ? 'not-allowed' : 'pointer',
              transition: 'background 0.3s ease',
              flexShrink: 0,
              opacity: verified ? 0.6 : 1,
            }}
            title={verified ? 'Already verified - toggle disabled' : ''}
          >
            <div
              style={{
                width: '22px',
                height: '22px',
                background: '#fff',
                borderRadius: '50%',
                position: 'absolute',
                top: '2px',
                left: verified ? '26px' : (ageVerificationEnabled ? '26px' : '2px'),
                transition: 'left 0.3s ease',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
              }}
            />
          </div>
          
          {/* Expanded content - shown when expanded */}
          <div
            className="toggle-expanded-content"
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: '8px',
              opacity: toggleExpanded ? 1 : 0,
              transition: 'opacity 0.2s ease 0.1s',
              whiteSpace: 'nowrap',
              pointerEvents: toggleExpanded ? 'auto' : 'none',
            }}
          >
            <label
              style={{
                fontSize: '0.85rem',
                fontWeight: '600',
                color: '#333',
                margin: 0,
              }}
            >
              Age Verification
            </label>
            <span
              style={{
                fontSize: '0.75rem',
                color: verified ? '#6c757d' : (ageVerificationEnabled ? '#28a745' : '#666'),
                fontWeight: '500',
              }}
            >
              {verified ? 'Already Verified' : (ageVerificationEnabled ? 'Enabled' : 'Disabled')}
            </span>
          </div>
        </div>
      </div>

      <div className="container mb-4">
        <h5 className="mb-3">Videos For Purchase</h5>
        <div className="row">
          {Object.values(videoCatalog).map((vid) => {
            const requiresVerification = vid.restricted && ageVerificationEnabled && !verified;
            return (
              <div className="col-md-4 mb-3 " key={vid.id} onClick={requiresVerification ? handleAge : undefined}>
                <div className="card card-video">
                  <img src={vid.thumb} className={["thumb", requiresVerification ? "blured" : ""].join(" ")} alt={vid.name} />
                  <div className={["card-body", requiresVerification ? "blured" : ""].join(" ")}>
                    <div className="mb-1 text-secondary small">{vid.duration}</div>
                    <h6 className="card-title">{vid.name}</h6>
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="price-tag">${vid.price}</div>
                    </div>
                    <div className="mt-3 d-flex align-items-center justify-content-between">
                      <div className="d-flex justify-content-between align-items-center">
                        {cart?.find((v) => v.id === vid.id) && (
                          <span className="badge bg-success px-3 py-2 rounded-pill shadow-sm">
                            In cart: <strong>{cart?.find((v) => v.id === vid.id)?.quantity}</strong>
                          </span>
                        )}
                      </div>
                      <button
                        className="btn btn-outline-primary btn-cart"
                        disabled={requiresVerification}
                        onClick={() => addToCart(vid.id)}
                      >
                        <FontAwesomeIcon icon={faCartShopping} /> Add to Cart
                      </button>
                    </div>
                  </div>
                  {requiresVerification && (
                    <div className="locked">
                      <FontAwesomeIcon icon={faLock} style={{ color: '#ffffff' }} />
                      <div className="small mt-2 text-warning">Age verification required</div>
                    </div>
                  )}
                  <span className="premium-badge">PREMIUM</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
